import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Image as ImageIcon, X } from 'lucide-react';
import MentionAutocomplete from './MentionAutocomplete';

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const CreatePost = ({ token, API, t, isRTL, onCreated, onCancel }) => {
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null); // image | video
  const [mediaDataUrl, setMediaDataUrl] = useState(null);
  const [caption, setCaption] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [alsoStory, setAlsoStory] = useState(false);
  const [success, setSuccess] = useState(null); // { msg, tone: 'ok' | 'warn' }
  const fileRef = useRef(null);
  const captionRef = useRef(null);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isImage = f.type.startsWith('image/');
    const isVideo = f.type.startsWith('video/');
    if (!isImage && !isVideo) {
      alert(t('mediaRequired'));
      return;
    }
    const dataUrl = await fileToDataUrl(f);
    setMediaDataUrl(dataUrl);
    setMediaPreview(dataUrl);
    setMediaType(isImage ? 'image' : 'video');
  };

  const clearMedia = () => {
    setMediaDataUrl(null);
    setMediaPreview(null);
    setMediaType(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!mediaDataUrl) {
      alert(t('mediaRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(
        `${API}/posts`,
        {
          media_url: mediaDataUrl,
          media_type: mediaType,
          caption: caption || null,
        },
        { headers }
      );

      let storyShared = false;
      let storyError = false;
      if (alsoStory) {
        try {
          await axios.post(
            `${API}/stories`,
            { media_url: mediaDataUrl, media_type: mediaType },
            { headers }
          );
          storyShared = true;
        } catch {
          storyError = true;
        }
      }

      const msg = storyError
        ? t('storyShareFailed')
        : storyShared
        ? t('postCreatedAndStoryShared')
        : t('postCreated');
      setSuccess({ msg, tone: storyError ? 'warn' : 'ok' });
      // Brief in-place confirmation, then hand off to parent (which navigates back to feed).
      setTimeout(() => {
        onCreated && onCreated(res.data);
      }, 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = t('error');
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail))
        msg = detail.map((d) => d?.msg || JSON.stringify(d)).join(', ');
      else if (detail) msg = JSON.stringify(detail);
      alert(msg);
      setSubmitting(false);
    }
    // NOTE: on success, leave submitting=true so the button stays disabled
    // during the 1.2s success-banner delay; parent then unmounts the screen.
  };

  return (
    <div
      data-testid="create-post-screen"
      dir={isRTL ? 'rtl' : 'ltr'}
      className="max-w-xl mx-auto px-4 pt-4 pb-6"
    >
      <h2 className="text-xl font-bold text-gray-900 mb-4">{t('createPost')}</h2>

      <form onSubmit={submit} className="space-y-4">
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-4">
          {!mediaPreview && (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="create-post-pick-media"
              className="w-full flex flex-col items-center justify-center gap-2 py-12 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
            >
              <ImageIcon className="w-10 h-10" />
              <span className="text-sm font-medium">{t('pickMedia')}</span>
            </button>
          )}

          {mediaPreview && (
            <div className="relative">
              {mediaType === 'video' ? (
                <video
                  src={mediaPreview}
                  controls
                  className="w-full max-h-96 rounded-lg bg-black"
                />
              ) : (
                <img
                  src={mediaPreview}
                  alt="preview"
                  className="w-full max-h-96 object-contain rounded-lg"
                />
              )}
              <button
                type="button"
                onClick={clearMedia}
                data-testid="create-post-clear-media"
                className="absolute top-2 end-2 bg-black/60 text-white rounded-full p-1.5 hover:bg-black/80"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          <input
            ref={fileRef}
            type="file"
            accept="image/*,video/*"
            className="hidden"
            onChange={onPick}
            data-testid="create-post-file-input"
          />
        </div>

        <div className="relative">
          <MentionAutocomplete
            inputRef={captionRef}
            value={caption}
            onChange={(next) => setCaption(next)}
            token={token}
            API={API}
            isRTL={isRTL}
            testIdPrefix="create-post-mention"
          />
          <textarea
            ref={captionRef}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder={t('captionPlaceholder')}
            data-testid="create-post-caption"
            rows={3}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-purple-400 bg-white"
          />
          <p
            data-testid="create-post-caption-hint"
            className="mt-1 px-1 text-[11px] text-gray-400"
          >
            {t('captionHint')}
          </p>
        </div>

        <label
          htmlFor="also-story-toggle"
          data-testid="create-post-also-story-row"
          className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-4 py-3 cursor-pointer select-none"
        >
          <span className="text-sm font-medium text-gray-800">{t('alsoShareToStory')}</span>
          <input
            id="also-story-toggle"
            type="checkbox"
            checked={alsoStory}
            onChange={(e) => setAlsoStory(e.target.checked)}
            data-testid="create-post-also-story-toggle"
            className="w-5 h-5 accent-purple-600"
          />
        </label>

        {success && (
          <div
            data-testid="create-post-success"
            className={`text-sm rounded-xl px-4 py-3 ${
              success.tone === 'warn'
                ? 'bg-amber-50 border border-amber-200 text-amber-800'
                : 'bg-green-50 border border-green-200 text-green-800'
            }`}
          >
            {success.msg}
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting || !mediaDataUrl}
            data-testid="create-post-submit"
            className="flex-1 py-3 rounded-xl bg-purple-600 text-white font-semibold hover:bg-purple-700 disabled:opacity-50 transition-colors"
          >
            {submitting ? t('publishing') : t('publish')}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              data-testid="create-post-cancel"
              className="py-3 px-5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              {t('cancel')}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default CreatePost;
